import {
  MutationFunction,
  gql,
  graphql,
  useMutation
} from "@outschool/ui-apollo";
import { MeetingFragment } from "@outschool/ui-components-classroom";
import React from "react";

import { validOptimisticResponse } from "../../shared/client/ApolloClient";
import { ActivityFragment } from "../../shared/client/fragments/ActivityFragments";
import {
  UpdateSectionMutation,
  UpdateSectionMutationVariables
} from "../../shared/generated/graphql";
import MessageFragment from "./MessageFragment";
import { SectionFragment, SectionLeaderFragment } from "./SectionFragments";

import type { ExecutionResult } from "@outschool/ui-apollo";

export const createSectionMutation = gql`
  mutation createSection($activityUid: ID!, $attribution: Attribution) {
    createSection(activityUid: $activityUid, attribution: $attribution) {
      filledSpaceCount
      ...SectionFragment
      ...SectionLeaderFragment
      meetings {
        ...MeetingFragment
      }
      activity {
        ...ActivityFragment
      }
      leader {
        uid
        is_crosslister
      }
    }
  }
  ${SectionFragment}
  ${SectionLeaderFragment}
  ${MeetingFragment}
  ${ActivityFragment}
`;

export const createSectionMutationDecorator = graphql(createSectionMutation, {
  name: "createSection"
});

export const updateSectionMutation = gql`
  mutation updateSection(
    $uid: ID!
    $updates: LeaderSectionInput
    $details: LeaderSectionDetailInput
    $onlineClassroom: OnlineClassroomInput
  ) {
    updateSection(
      uid: $uid
      updates: $updates
      details: $details
      onlineClassroom: $onlineClassroom
    ) {
      filledSpaceCount
      ...SectionFragment
      ...SectionLeaderFragment
      meetings {
        ...MeetingFragment
      }
      leader {
        uid
        is_crosslister
      }
    }
  }
  ${SectionFragment}
  ${SectionLeaderFragment}
  ${MeetingFragment}
`;

type SectionMutationFunction = MutationFunction<
  UpdateSectionMutation,
  UpdateSectionMutationVariables
>;

export const updateSectionMutationDecorator = graphql(updateSectionMutation, {
  // No point in making this typecheck since we're migrating away from its usage
  // @ts-ignore
  props: ({ mutate }: { mutate: SectionMutationFunction }) => ({
    updateSection: ({
      variables: { section, updates, details, onlineClassroom }
    }: $TSFixMe) => {
      return mutateHelper(mutate, {
        section,
        updates,
        details,
        onlineClassroom
      });
    }
  })
});

type UpdateSectionMutationArgs = Pick<
  UpdateSectionMutationVariables,
  "updates" | "details" | "onlineClassroom"
> & { section: any };

export function useUpdateSectionMutation() {
  const [mutate, { loading }] = useMutation<
    UpdateSectionMutation,
    UpdateSectionMutationVariables
  >(updateSectionMutation);
  const mutateFn = React.useCallback(
    ({
      section,
      updates,
      details,
      onlineClassroom
    }: UpdateSectionMutationArgs) =>
      mutateHelper(mutate, {
        section,
        updates,
        details,
        onlineClassroom
      }),
    [mutate]
  );
  return { mutate: mutateFn, loading };
}

function mutateHelper(
  mutate: SectionMutationFunction,
  { section, updates, details, onlineClassroom }: UpdateSectionMutationArgs
) {
  const spreadUpdates = Object.assign({}, section, updates || {});
  const spreadDetails = Object.assign({}, section.details, details || {});
  if (spreadUpdates.isPublished === true) {
    spreadUpdates.published_at = new Date();
  } else if (spreadUpdates.isPublished === false) {
    spreadUpdates.published_at = null;
  }
  let mutateParams = {
    variables: {
      uid: section.uid,
      updates,
      details,
      onlineClassroom
    }
  };
  /**
   * updating leader uid (user_uid) causes a race condition on ios because
   * the section object contains 'leader' object, not 'user_uid', so optimisticResponse overwrites
   * the selection dropdown value with the old teacher name.
   * Therefore do not use optimistic results when trying to update the leader uid.
   *  */
  if (!updates?.user_uid) {
    // @ts-ignore
    mutateParams["optimisticResponse"] = validOptimisticResponse({
      __typename: "Mutation",
      updateSection: {
        __typename: "Section",
        ...spreadUpdates,
        details: {
          __typename: "SectionDetails",
          ...spreadDetails,
          onlineClassroom: {
            __typename: "OnlineClassroom",
            ...(onlineClassroom || section.details.onlineClassroom)
          }
        }
      }
    });
  }
  return mutate(mutateParams);
}

export const stopOngoingSectionMutation = gql`
  mutation stopOngoingSectionMutation(
    $sectionUid: ID!
    $stopAfter: DateTime
    $cancellationNote: String
    $refundAmountCents: Int
    $reducesTeacherPayout: Boolean
  ) {
    stopOngoingSection(
      sectionUid: $sectionUid
      stopAfter: $stopAfter
      cancellationNote: $cancellationNote
      refundAmountCents: $refundAmountCents
      reducesTeacherPayout: $reducesTeacherPayout
    ) {
      filledSpaceCount
      ...SectionFragment
      ...SectionLeaderFragment
      meetings {
        ...MeetingFragment
      }
    }
  }
  ${SectionFragment}
  ${SectionLeaderFragment}
  ${MeetingFragment}
`;

export const cancelSectionMutation = gql`
  mutation cancelSectionMutation(
    $uid: ID!
    $cancellationNote: String
    $withdraw: Boolean
    $refundAmountCents: Int
    $reducesTeacherPayout: Boolean
  ) {
    cancelSection(
      uid: $uid
      cancellationNote: $cancellationNote
      withdraw: $withdraw
      refundAmountCents: $refundAmountCents
      reducesTeacherPayout: $reducesTeacherPayout
    ) {
      filledSpaceCount
      ...SectionFragment
      ...SectionLeaderFragment
      meetings {
        ...MeetingFragment
      }
    }
  }
  ${SectionFragment}
  ${SectionLeaderFragment}
  ${MeetingFragment}
`;

export const refundAllEnrollmentsInSectionMutation = gql`
  mutation refundAllEnrollmentsInSection(
    $uid: ID!
    $cancellationNote: String
    $shouldWithdrawEnrollments: Boolean
    $refundAmountCents: Int
    $reducesTeacherPayout: Boolean
  ) {
    refundAllEnrollmentsInSection(
      uid: $uid
      cancellationNote: $cancellationNote
      shouldWithdrawEnrollments: $shouldWithdrawEnrollments
      refundAmountCents: $refundAmountCents
      reducesTeacherPayout: $reducesTeacherPayout
    ) {
      filledSpaceCount
      ...SectionFragment
      ...SectionLeaderFragment
      meetings {
        ...MeetingFragment
      }
    }
  }
  ${SectionFragment}
  ${SectionLeaderFragment}
  ${MeetingFragment}
`;

export function useGenerateProgressReportMutation(): ({
  sectionUid,
  notes,
  reportLength
}: {
  sectionUid: string;
  notes: string;
  reportLength: string;
}) => Promise<ExecutionResult<any>> {
  const [generateProgressReport] = useMutation(
    gql`
      mutation generateProgressReport(
        $sectionUid: ID!
        $notes: String!
        $reportLength: ReportLength
      ) {
        generateProgressReport(
          sectionUid: $sectionUid
          notes: $notes
          reportLength: $reportLength
        )
      }
    `
  );

  return React.useCallback(
    ({
      sectionUid,
      notes,
      reportLength
    }: {
      sectionUid: string;
      notes: string;
      reportLength: string;
    }) =>
      generateProgressReport({
        variables: {
          sectionUid,
          notes,
          reportLength
        }
      }),
    [generateProgressReport]
  );
}

export function useSendProgressReportMutation(): [
  ({
    sectionUid,
    learnerUid,
    text,
    details
  }: {
    sectionUid: string;
    learnerUid?: string;
    text: string;
    details: {
      originalNotes: string;
      reportLength: string;
      didGenerateReport: Boolean;
      didChangeGeneratedReport: Boolean;
    };
  }) => Promise<ExecutionResult<any>>,
  { isSendingProgressReport: boolean }
] {
  const [sendProgressReport, { loading }] = useMutation(
    gql`
      mutation sendProgressReport(
        $sectionUid: ID!
        $learnerUid: ID
        $text: String!
        $details: ProgressReportDetailsInput!
      ) {
        sendProgressReport(
          sectionUid: $sectionUid
          learnerUid: $learnerUid
          text: $text
          details: $details
        ) {
          ...MessageFragment
        }
      }
      ${MessageFragment}
    `
  );

  return [
    React.useCallback(
      ({
        sectionUid,
        learnerUid,
        text,
        details
      }: {
        sectionUid: string;
        learnerUid: string;
        text: string;
        details: {
          originalNotes: string;
          reportLength: string;
          didGenerateReport: Boolean;
          didChangeGeneratedReport: Boolean;
        };
      }) =>
        sendProgressReport({
          variables: {
            sectionUid,
            learnerUid,
            text,
            details
          }
        }),
      [sendProgressReport]
    ),
    { isSendingProgressReport: loading }
  ];
}
